import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';

// Fixed palette for chart series (recharts draws SVG, where CSS var() won't resolve).
const PALETTE = ['#2dd4bf', '#60a5fa', '#10b981', '#f59e0b', '#a78bfa', '#f472b6', '#84cc16', '#38bdf8', '#fbbf24', '#94a3b8'];
const DOC_LABEL: Record<string, string> = {
  inventory: 'Inventory', valuation: 'Valuation', 'asset-management-plan': 'Asset Mgmt Plan',
  'financial-report': 'Financial Report', policy: 'Policy', strategy: 'Strategy',
  'case-study': 'Case Study', funding: 'Funding', news: 'News', other: 'Other', unspecified: 'Unspecified',
};
const label = (k: string) => DOC_LABEL[k] || k;

type Overview = { totalRelevant: number; organizations: number; provinces: number; outputTypeCount: number; maladaptationCount: number; span: { from: string; to: string } | null };
type Series = { groupBy: string; bucket: string; seriesKeys: string[]; series: { period: string; total: number; breakdown: Record<string, number> }[] };
type Region = { province: string; total: number; byType: Record<string, number>; recent: number; prior: number; growthPct: number | null };
type Theme = { tag: string; recent: number; baseline: number; deltaPct: number | null; direction: 'up' | 'down' | 'flat' };

const Kpi = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[14px] px-4 py-3 min-w-[120px]">
    <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)]">{label}</div>
    <div className="text-[22px] font-semibold text-[color:var(--text-primary)] mt-1">{value}</div>
  </div>
);

const Panel: React.FC<{ title: string; children: React.ReactNode; note?: string }> = ({ title, children, note }) => (
  <div className="bg-[var(--glass-bg)] backdrop-blur-[12px] border border-[var(--glass-border)] rounded-[16px] p-5 mb-6">
    <div className="text-[14px] font-semibold text-[color:var(--text-primary)] mb-1">{title}</div>
    {note && <div className="text-[12px] text-[color:var(--text-muted)] mb-3">{note}</div>}
    {children}
  </div>
);

export default function AnalyticsDashboard() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [ts, setTs] = useState<Series | null>(null);
  const [regions, setRegions] = useState<Region[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [provinces, setProvinces] = useState<string[]>([]);

  const [groupBy, setGroupBy] = useState('documentType');
  const [bucket, setBucket] = useState('month');
  const [province, setProvince] = useState('');

  useEffect(() => {
    axios.get('/api/analytics/overview').then(r => setOverview(r.data)).catch(console.error);
    axios.get('/api/analytics/regions').then(r => setRegions(r.data.regions)).catch(console.error);
    axios.get('/api/analytics/themes/trending').then(r => setThemes(r.data.themes)).catch(console.error);
    axios.get('/api/library/provinces').then(r => setProvinces(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const qp = new URLSearchParams({ groupBy, bucket });
    if (province) qp.set('province', province);
    axios.get(`/api/analytics/timeseries?${qp}`).then(r => setTs(r.data)).catch(console.error);
  }, [groupBy, bucket, province]);

  // Flatten series for recharts: one row per period with each series key as a field.
  const chartData = useMemo(() => {
    if (!ts) return [];
    return ts.series.map(s => groupBy === 'none'
      ? { period: s.period, total: s.total }
      : { period: s.period, ...ts.seriesKeys.reduce((o, k) => ({ ...o, [k]: s.breakdown[k] || 0 }), {}) });
  }, [ts, groupBy]);

  const areaKeys = groupBy === 'none' ? ['total'] : (ts?.seriesKeys || []);

  // Stacked region×type bar data.
  const regionTypeKeys = useMemo(() => {
    const s = new Set<string>();
    regions.forEach(r => Object.keys(r.byType).forEach(k => s.add(k)));
    return Array.from(s);
  }, [regions]);
  const regionData = regions.map(r => ({ province: r.province, ...r.byType }));

  const axisStyle = { fontSize: 11, fill: 'var(--text-muted)' };
  const tooltipStyle = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12, color: 'var(--text-primary)' };

  return (
    <div className="bg-transparent min-h-full p-8">
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold text-[color:var(--text-primary)] m-0 mb-1">Trends</h1>
        <p className="text-[13px] text-[color:var(--text-muted)] m-0">Aggregate signals across the knowledge base — activity over time, regional momentum, and emerging themes.</p>
      </div>

      {/* KPI strip */}
      {overview && (
        <div className="flex flex-wrap gap-3 mb-6">
          <Kpi label="Relevant items" value={overview.totalRelevant} />
          <Kpi label="Organizations" value={overview.organizations} />
          <Kpi label="Provinces" value={overview.provinces} />
          <Kpi label="Output types" value={overview.outputTypeCount} />
          <Kpi label="Maladaptation" value={overview.maladaptationCount} />
        </div>
      )}

      {/* Honesty disclosure */}
      <div className="flex items-start gap-2 text-[12px] text-[color:var(--text-muted)] bg-[var(--surface)] border border-[var(--border)] rounded-[10px] px-3 py-2 mb-6">
        <Info size={14} className="mt-[1px] shrink-0" />
        <span>Trends are bucketed by publication date where known, falling back to crawl date (~44% of items). Recent buckets are weighted by when content was ingested, so read these as directional until more dated content accumulates.</span>
      </div>

      {/* Activity over time */}
      <Panel title="Activity over time">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className="bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-1 text-[12px] text-[color:var(--text-primary)]">
            <option value="none">Total</option>
            <option value="documentType">By output type</option>
            <option value="province">By province</option>
            <option value="signal">By signal</option>
          </select>
          <select value={bucket} onChange={e => setBucket(e.target.value)} className="bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-1 text-[12px] text-[color:var(--text-primary)]">
            <option value="month">Monthly</option>
            <option value="quarter">Quarterly</option>
          </select>
          <select value={province} onChange={e => setProvince(e.target.value)} className="bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-1 text-[12px] text-[color:var(--text-primary)]">
            <option value="">All provinces</option>
            {provinces.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="period" tick={axisStyle} stroke="var(--border)" />
            <YAxis tick={axisStyle} stroke="var(--border)" allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: 'var(--text-primary)' }} formatter={((v: any, n: any) => [v, label(String(n))]) as any} />
            {groupBy !== 'none' && <Legend formatter={(v: string) => label(v)} wrapperStyle={{ fontSize: 11 }} />}
            {areaKeys.map((k, i) => (
              <Area key={k} type="monotone" dataKey={k} stackId="1" stroke={PALETTE[i % PALETTE.length]} fill={PALETTE[i % PALETTE.length]} fillOpacity={0.5} name={k} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </Panel>

      {/* Output types by region */}
      <Panel title="Output types by region">
        <ResponsiveContainer width="100%" height={Math.max(180, regions.length * 48)}>
          <BarChart data={regionData} layout="vertical" margin={{ top: 5, right: 10, left: 30, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis type="number" tick={axisStyle} stroke="var(--border)" allowDecimals={false} />
            <YAxis type="category" dataKey="province" tick={axisStyle} stroke="var(--border)" width={120} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'var(--border)', opacity: 0.3 }} formatter={((v: any, n: any) => [v, label(String(n))]) as any} />
            <Legend formatter={(v: string) => label(v)} wrapperStyle={{ fontSize: 11 }} />
            {regionTypeKeys.map((k, i) => (
              <Bar key={k} dataKey={k} stackId="a" fill={PALETTE[i % PALETTE.length]} name={k} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <div className="grid gap-6 [grid-template-columns:minmax(0,1fr)] lg:[grid-template-columns:minmax(0,1fr)_minmax(0,1fr)]">
        {/* Regional momentum */}
        <Panel title="Regional momentum" note="New items in the last 90 days vs the prior 90 days.">
          <div className="flex flex-col gap-2">
            {regions.map(r => (
              <div key={r.province} className="flex items-center justify-between text-[13px] border-b border-[var(--border)] pb-2 last:border-0">
                <span className="text-[color:var(--text-primary)]">{r.province}</span>
                <div className="flex items-center gap-3 text-[color:var(--text-muted)]">
                  <span>{r.total} total</span>
                  <span className="inline-flex items-center gap-1 w-[88px] justify-end">
                    {r.growthPct === null ? <span className="text-[#10b981]">new</span>
                      : r.growthPct > 0 ? <><TrendingUp size={14} className="text-[#10b981]" /><span className="text-[#10b981]">+{r.growthPct}%</span></>
                      : r.growthPct < 0 ? <><TrendingDown size={14} className="text-[#f472b6]" /><span className="text-[#f472b6]">{r.growthPct}%</span></>
                      : <><Minus size={14} /><span>0%</span></>}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        {/* Trending themes */}
        <Panel title="Trending themes" note="Tag frequency in the last 90 days vs the prior 90 days.">
          <div className="flex flex-col gap-2">
            {themes.slice(0, 12).map(t => (
              <div key={t.tag} className="flex items-center justify-between text-[13px] border-b border-[var(--border)] pb-2 last:border-0">
                <span className="text-[color:var(--text-primary)]">{t.tag}</span>
                <div className="flex items-center gap-3 text-[color:var(--text-muted)]">
                  <span>{t.recent}</span>
                  <span className="inline-flex items-center gap-1 w-[72px] justify-end">
                    {t.deltaPct === null ? <span className="text-[#10b981]">new</span>
                      : t.direction === 'up' ? <><TrendingUp size={14} className="text-[#10b981]" /><span className="text-[#10b981]">+{t.deltaPct}%</span></>
                      : t.direction === 'down' ? <><TrendingDown size={14} className="text-[#f472b6]" /><span className="text-[#f472b6]">{t.deltaPct}%</span></>
                      : <><Minus size={14} /><span>0%</span></>}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
